"use client";

import { useEffect, useMemo, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { NAV_SECTIONS, NAV_SECTION_EVENT, NAV_SECTION_STORAGE_KEY, pickLocaleText, resolveNavSectionKey } from "./navStructure";
import { useUiLocale } from "./useUiLocale";

export default function Sidebar() {
  const path = usePathname() || "/";
  const search = useSearchParams();
  const locale = useUiLocale();
  let pathSectionKey = resolveNavSectionKey(path);
  if (path.startsWith("/invoices/inbox")) {
    const scope = search?.get("scope");
    const view = search?.get("view");
    if (scope === "debtor") pathSectionKey = "verkauf";
    else if (scope === "creditor") pathSectionKey = "einkauf";
    else if (view === "automation") pathSectionKey = "automation";
  }
  const [sectionKey, setSectionKey] = useState<string>(pathSectionKey);

  useEffect(() => {
    // Keep sidebar in sync with actual route (e.g. /admin should always show Admin items).
    setSectionKey(pathSectionKey);
  }, [pathSectionKey]);

  useEffect(() => {
    const onSectionChange = (evt: Event) => {
      const key = (evt as CustomEvent<{ key?: string }>).detail?.key;
      if (key && NAV_SECTIONS.some((s) => s.key === key)) {
        setSectionKey(key);
        try {
          localStorage.setItem(NAV_SECTION_STORAGE_KEY, key);
        } catch {
          // Ignore storage failures.
        }
      }
    };
    window.addEventListener(NAV_SECTION_EVENT, onSectionChange as EventListener);
    return () => window.removeEventListener(NAV_SECTION_EVENT, onSectionChange as EventListener);
  }, []);

  const section = useMemo(
    () => NAV_SECTIONS.find((s) => s.key === sectionKey) || NAV_SECTIONS[0],
    [sectionKey]
  );

  function isActiveHref(href?: string): boolean {
    if (!href) return false;
    const [targetPath, targetQuery] = href.split("?");
    if (!path.startsWith(targetPath)) return false;
    if (!targetQuery) return true;
    const targetParams = new URLSearchParams(targetQuery);
    for (const [k, v] of targetParams.entries()) {
      if ((search?.get(k) || "") !== v) return false;
    }
    return true;
  }

  return (
    <div className="sidebar">
      <div className="sidebar-section">
        <div className="sidebar-title">
          {pickLocaleText(locale, section.sidebarTitle || section.title, section.sidebarTitleI18n || section.titleI18n)}
        </div>
        <div className="sidebar-items">
          {section.items.map((it) => {
            const active = isActiveHref(it.href);
            if (!it.href) {
              return (
                <span key={it.label} className="sidebar-link is-disabled">
                  <span className="sidebar-link-row">
                    <span>{pickLocaleText(locale, it.label, it.i18n)}</span>
                  </span>
                </span>
              );
            }
            return (
                <a
                  key={it.label}
                  href={it.href}
                  className={`sidebar-link ${active ? "is-active" : ""}`}
                >
                  <span className="sidebar-link-row">
                    <span>{pickLocaleText(locale, it.label, it.i18n)}</span>
                  </span>
                </a>
            );
          })}
        </div>
      </div>
    </div>
  );
}
